/**
 * Room Detail Panel — Inline right-side panel
 *
 * Shows comprehensive room information:
 * - Current Stay tab: guest info + full activity timeline
 * - Past Stays tab: historical stays with expandable activity logs
 *
 * This is NOT an overlay/drawer — it sits inline in the master-detail layout.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ActivityRecord } from '@/types/activityLedger';
import {
  RoomDirectoryEntry,
  RoomHistoryEntry,
  fetchRoomHistory,
  fetchActivityLedger,
} from '@/staff/services/activityLedgerService';

// =============================================================================
// TYPES
// =============================================================================

interface RoomDetailPanelProps {
  room: RoomDirectoryEntry;
  activities: ActivityRecord[];
  onClose: () => void;
}

type TabId = 'current' | 'history';

// =============================================================================
// CONFIG
// =============================================================================

const ACTIVITY_CONFIG: Record<string, { icon: string; color: string; bg: string; label: string }> = {
  BOOKING: { icon: '📅', color: 'text-blue-400', bg: 'bg-blue-900/30', label: 'Booking' },
  REQUEST: { icon: '🎯', color: 'text-purple-400', bg: 'bg-purple-900/30', label: 'Request' },
  ECO: { icon: '🌱', color: 'text-green-400', bg: 'bg-green-900/30', label: 'Eco' },
  HOUSEKEEPING: { icon: '🧹', color: 'text-cyan-400', bg: 'bg-cyan-900/30', label: 'Housekeeping' },
  ORDER: { icon: '🍽️', color: 'text-orange-400', bg: 'bg-orange-900/30', label: 'Order' },
  MAINTENANCE: { icon: '🔧', color: 'text-red-400', bg: 'bg-red-900/30', label: 'Maintenance' },
  SYSTEM: { icon: '⚙️', color: 'text-slate-400', bg: 'bg-slate-900/30', label: 'System' },
};

const STATUS_COLORS: Record<string, string> = {
  NEW: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
  PENDING: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
  IN_PROGRESS: 'bg-blue-900/30 text-blue-300 border-blue-700',
  COMPLETED: 'bg-emerald-900/30 text-emerald-300 border-emerald-700',
  CONFIRMED: 'bg-emerald-900/30 text-emerald-300 border-emerald-700',
  ACTIVE: 'bg-emerald-900/30 text-emerald-300 border-emerald-700',
  CHECKED_IN: 'bg-blue-900/30 text-blue-300 border-blue-700',
  CHECKED_OUT: 'bg-slate-800 text-slate-400 border-slate-600',
  CANCELLED: 'bg-red-900/30 text-red-300 border-red-700',
};

// =============================================================================
// COMPONENT
// =============================================================================

export function RoomDetailPanel({ room, activities, onClose }: RoomDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>('current');

  const guestName = room.guest
    ? `${room.guest.firstName} ${room.guest.lastName}`
    : null;

  // Current stay activities (matching the room's current stayId)
  const currentStayActivities = useMemo(() => {
    if (!room.stay) return [];
    return activities
      .filter(a => a.stayId === room.stay!.stayId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [activities, room.stay]);

  // Stats for current stay
  const stats = useMemo(() => {
    return {
      pendingRequests: currentStayActivities.filter(a => a.type === 'REQUEST' && (a.status === 'PENDING' || a.status === 'NEW')).length,
      greenStay: currentStayActivities.some(a => a.type === 'ECO' && a.status === 'ACTIVE'),
      totalActivities: currentStayActivities.length,
      orders: currentStayActivities.filter(a => a.type === 'ORDER').length,
      housekeeping: currentStayActivities.filter(a => a.type === 'HOUSEKEEPING').length,
    };
  }, [currentStayActivities]);

  const roomStatusConfig: Record<string, { label: string; color: string; dot: string }> = {
    OCCUPIED: { label: 'Occupied', color: 'text-emerald-400', dot: 'bg-emerald-400' },
    VACANT: { label: 'Vacant', color: 'text-slate-400', dot: 'bg-slate-400' },
    CLEANING: { label: 'Cleaning', color: 'text-yellow-400', dot: 'bg-yellow-400' },
    OUT_OF_SERVICE: { label: 'Out of Service', color: 'text-red-400', dot: 'bg-red-400' },
  };

  const statusInfo = roomStatusConfig[room.status] ?? roomStatusConfig.VACANT!;

  return (
    <div className="h-full flex flex-col bg-slate-800 border-l border-slate-700">
      {/* Header */}
      <div className="bg-slate-900 px-6 py-5 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-white">Room {room.roomNumber}</h2>
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${statusInfo.dot}`} />
                <span className={`text-sm font-medium ${statusInfo.color}`}>{statusInfo.label}</span>
              </div>
            </div>
            {guestName && (
              <p className="text-slate-300 text-lg mt-1">{guestName}</p>
            )}
            {room.stay && (
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                <span>📅 {formatDate(room.stay.checkInDate)} → {formatDate(room.stay.checkOutDate)}</span>
                <span className="text-xs text-slate-500">{room.roomType} • Floor {room.floor}</span>
              </div>
            )}
            {!room.stay && (
              <p className="text-slate-500 mt-1">No active guest</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/rooms/${room.roomNumber}`}
              className="text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30 px-2.5 py-1.5 rounded-lg transition-colors font-medium"
            >
              View Timeline →
            </Link>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg p-2 transition"
              aria-label="Close panel"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Quick Stats (only if occupied) */}
        {room.stay && (
          <div className="flex flex-wrap gap-2">
            {stats.pendingRequests > 0 && (
              <span className="px-2.5 py-1 bg-yellow-900/30 border border-yellow-700 text-yellow-300 text-xs font-medium rounded-full">
                🎯 {stats.pendingRequests} pending
              </span>
            )}
            {stats.greenStay && (
              <span className="px-2.5 py-1 bg-green-900/30 border border-green-700 text-green-300 text-xs font-medium rounded-full">
                🌱 Green Stay
              </span>
            )}
            {stats.orders > 0 && (
              <span className="px-2.5 py-1 bg-orange-900/30 border border-orange-700 text-orange-300 text-xs font-medium rounded-full">
                🍽️ {stats.orders} orders
              </span>
            )}
            <span className="px-2.5 py-1 bg-slate-700 border border-slate-600 text-slate-300 text-xs font-medium rounded-full">
              {stats.totalActivities} activities
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mt-4 border-b border-slate-700 -mx-6 px-6">
          <button
            onClick={() => setActiveTab('current')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'current'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            Current Stay
            {currentStayActivities.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-slate-700 rounded">{currentStayActivities.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            Past Stays
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'current' ? (
          <CurrentStayTab
            room={room}
            activities={currentStayActivities}
          />
        ) : (
          <PastStaysTab roomNumber={room.roomNumber} currentStayId={room.stay?.stayId} />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// CURRENT STAY TAB
// =============================================================================

function CurrentStayTab({ room, activities }: { room: RoomDirectoryEntry; activities: ActivityRecord[] }) {
  if (!room.stay) {
    return (
      <div className="p-6 text-center text-slate-400">
        <div className="text-4xl mb-3">🛏️</div>
        <p className="text-lg font-medium">Room is vacant</p>
        <p className="text-sm mt-1">No active stay for this room</p>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="p-6 text-center text-slate-400">
        <div className="text-4xl mb-3">📋</div>
        <p className="text-lg font-medium">No activities yet</p>
        <p className="text-sm mt-1">Activity will appear here as events occur</p>
      </div>
    );
  }

  return (
    <div className="p-5">
      <ActivityTimeline activities={activities} />
    </div>
  );
}

// =============================================================================
// PAST STAYS TAB
// =============================================================================

// =============================================================================
// PAST STAYS TAB — Search-First Design
// =============================================================================

const RECENT_LIMIT = 5;
const PAGE_SIZE = 10;

function PastStaysTab({ roomNumber, currentStayId }: { roomNumber: string; currentStayId?: string }) {
  // Search state
  const [guestSearch, setGuestSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Data state
  const [recentStays, setRecentStays] = useState<RoomHistoryEntry[]>([]);
  const [searchResults, setSearchResults] = useState<RoomHistoryEntry[] | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [totalStays, setTotalStays] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);

  // Expanded stay (for viewing activity timeline)
  const [expandedStayId, setExpandedStayId] = useState<string | null>(null);
  const [expandedActivities, setExpandedActivities] = useState<ActivityRecord[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Load recent stays on mount
  useEffect(() => {
    let cancelled = false;
    async function loadRecent() {
      try {
        setLoading(true);
        const data = await fetchRoomHistory(roomNumber, { limit: RECENT_LIMIT + 1 }); // +1 to exclude current stay
        if (cancelled) return;
        // Exclude current stay from results
        const filtered = data.stays.filter(s => s.stayId !== currentStayId);
        setRecentStays(filtered.slice(0, RECENT_LIMIT));
        setTotalStays(currentStayId ? data.total - 1 : data.total); // subtract current stay from total
      } catch (err) {
        console.error('[PastStays] Failed to load recent:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadRecent();
    return () => { cancelled = true; };
  }, [roomNumber, currentStayId]);

  // Execute search
  const executeSearch = useCallback(async (page = 1) => {
    if (!guestSearch.trim() && !dateFrom && !dateTo) {
      // Clear search, show recent
      setSearchResults(null);
      setIsSearching(false);
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearching(true);
      setCurrentPage(page);

      const data = await fetchRoomHistory(roomNumber, {
        guest: guestSearch.trim() || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        page,
        limit: PAGE_SIZE,
      });

      // Exclude current stay
      const filtered = data.stays.filter(s => s.stayId !== currentStayId);
      setSearchResults(filtered);
      setTotalResults(currentStayId ? Math.max(data.total - 1, 0) : data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('[PastStays] Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [roomNumber, currentStayId, guestSearch, dateFrom, dateTo]);

  // Load activities for expanded stay
  const toggleExpandStay = useCallback(async (stayId: string) => {
    if (expandedStayId === stayId) {
      setExpandedStayId(null);
      setExpandedActivities([]);
      return;
    }

    setExpandedStayId(stayId);
    setActivitiesLoading(true);
    try {
      const activities = await fetchActivityLedger({ stayId, limit: 200 });
      setExpandedActivities(
        activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      );
    } catch (err) {
      console.error('[PastStays] Failed to load activities for stay:', stayId, err);
      setExpandedActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [expandedStayId]);

  // Clear search
  const clearSearch = useCallback(() => {
    setGuestSearch('');
    setDateFrom('');
    setDateTo('');
    setSearchResults(null);
    setIsSearching(false);
    setCurrentPage(1);
  }, []);

  // Handle search form submit
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    executeSearch(1);
  }, [executeSearch]);

  // Loading state
  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500 mx-auto mb-3"></div>
        <p className="text-slate-500 text-sm">Loading stay history...</p>
      </div>
    );
  }

  // No past stays at all
  if (totalStays === 0 && !isSearching) {
    return (
      <div className="p-6 text-center text-slate-400">
        <div className="text-4xl mb-3">📁</div>
        <p className="text-lg font-medium">No past stays on record</p>
        <p className="text-sm mt-1">Historical stays will appear here after checkout</p>
      </div>
    );
  }

  const displayStays = isSearching ? (searchResults || []) : recentStays;

  return (
    <div className="flex flex-col h-full">
      {/* Search Controls */}
      <form onSubmit={handleSearchSubmit} className="p-4 border-b border-slate-700 flex-shrink-0 space-y-3">
        {/* Guest name search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={guestSearch}
            onChange={(e) => setGuestSearch(e.target.value)}
            placeholder="Search past guest name..."
            className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500"
          />
        </div>

        {/* Date range */}
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-1.5 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-emerald-500 [color-scheme:dark]"
            />
          </div>
          <div className="flex-1">
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-1.5 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-emerald-500 [color-scheme:dark]"
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={searchLoading}
            className="flex-1 px-3 py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
          >
            {searchLoading ? 'Searching...' : 'Search'}
          </button>
          {isSearching && (
            <button
              type="button"
              onClick={clearSearch}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {/* Results header */}
        <div className="px-4 pt-3 pb-2">
          <p className="text-xs text-slate-500">
            {isSearching
              ? `${totalResults} result${totalResults !== 1 ? 's' : ''} found`
              : `Recent stays (showing ${recentStays.length} of ${totalStays})`
            }
          </p>
        </div>

        {/* Stay list */}
        {displayStays.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">
            {isSearching ? 'No stays matching your search' : 'No past stays'}
          </div>
        ) : (
          <div className="px-4 pb-4 space-y-2">
            {displayStays.map(stay => {
              const isExpanded = expandedStayId === stay.stayId;
              return (
                <div key={stay.stayId} className="border border-slate-700 rounded-lg overflow-hidden">
                  {/* Stay Row */}
                  <button
                    onClick={() => toggleExpandStay(stay.stayId)}
                    className="w-full px-4 py-3 flex items-center gap-3 bg-slate-750 hover:bg-slate-700 transition-colors text-left"
                  >
                    {/* Guest avatar placeholder */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-xs font-bold text-slate-300">
                      {stay.guestName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{stay.guestName}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {formatDate(stay.checkInDate)} {'\u2192'} {formatDate(stay.checkOutDate)}
                        <span className="text-slate-600 ml-1">({stay.nights}n)</span>
                      </div>
                    </div>

                    {/* Status badge */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={'px-1.5 py-0.5 text-[10px] font-medium rounded border ' + (STATUS_COLORS[stay.status] || 'bg-slate-800 text-slate-400 border-slate-600')}>
                        {stay.status}
                      </span>
                      <svg
                        className={'w-4 h-4 text-slate-400 transition-transform ' + (isExpanded ? 'rotate-180' : '')}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Expanded Activity Timeline */}
                  {isExpanded && (
                    <div className="p-4 bg-slate-800/50 border-t border-slate-700">
                      {activitiesLoading ? (
                        <div className="text-center py-3">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-500 mx-auto mb-2"></div>
                          <p className="text-slate-500 text-xs">Loading activities...</p>
                        </div>
                      ) : expandedActivities.length === 0 ? (
                        <p className="text-slate-500 text-xs text-center py-2">No recorded activities for this stay</p>
                      ) : (
                        <ActivityTimeline activities={expandedActivities} compact />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination (only in search mode) */}
        {isSearching && totalPages > 1 && (
          <div className="px-4 pb-4 flex items-center justify-center gap-2">
            <button
              onClick={() => executeSearch(currentPage - 1)}
              disabled={currentPage <= 1 || searchLoading}
              className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 disabled:opacity-30 text-slate-300 rounded transition-colors"
            >
              Previous
            </button>
            <span className="text-xs text-slate-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => executeSearch(currentPage + 1)}
              disabled={currentPage >= totalPages || searchLoading}
              className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 disabled:opacity-30 text-slate-300 rounded transition-colors"
            >
              Next
            </button>
          </div>
        )}

        {/* "Search all" prompt (only in recent mode with more stays available) */}
        {!isSearching && totalStays > RECENT_LIMIT && (
          <div className="px-4 pb-4 text-center">
            <button
              onClick={() => {
                // Trigger empty search to show all results paginated
                setIsSearching(true);
                setSearchResults(null);
                executeSearch(1);
              }}
              className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              {'Search all ' + totalStays + ' past stays \u2192'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ACTIVITY TIMELINE
// =============================================================================

function getActivityStatusDisplay(activity: ActivityRecord): { label: string; color: string } {
  const defaultColor = STATUS_COLORS[activity.status] ?? 'bg-slate-800 text-slate-400 border-slate-600';

  if (activity.type === 'HOUSEKEEPING') {
    // Green Stay skips: origin=GREEN_STAY, category=SKIPPED, or greenStay flag
    if (
      activity.metadata?.greenStay === true ||
      activity.metadata?.origin === 'GREEN_STAY' ||
      activity.category === 'SKIPPED'
    ) {
      return { label: '🌿 Skipped (Green Stay)', color: 'bg-green-900/30 text-green-300 border-green-700' };
    }
    // Generic CANCELLED on a housekeeping task (e.g. superseded checkout clean) → neutral, not alarming red
    if (activity.status === 'CANCELLED') {
      return { label: 'Cancelled', color: 'bg-slate-800 text-slate-400 border-slate-600' };
    }
  }

  return { label: activity.status, color: defaultColor };
}

function ActivityTimeline({ activities, compact = false }: { activities: ActivityRecord[]; compact?: boolean }) {
  return (
    <div className="space-y-3">
      {activities.map((activity, index) => {
        const config = ACTIVITY_CONFIG[activity.type] ?? ACTIVITY_CONFIG.SYSTEM!;
        const { label: statusLabel, color: statusColor } = getActivityStatusDisplay(activity);
        const description = formatActivityDescription(activity);
        const isLast = index === activities.length - 1;

        return (
          <div key={activity.id} className="relative flex gap-3">
            {/* Timeline line */}
            {!isLast && (
              <div className="absolute left-3 top-8 w-px h-[calc(100%-8px)] bg-slate-700" />
            )}

            {/* Icon */}
            <div className={`flex-shrink-0 w-6 h-6 rounded-full ${config.bg} flex items-center justify-center text-xs z-10 border border-slate-700`}>
              {config.icon}
            </div>

            {/* Content */}
            <div className={`flex-1 ${compact ? 'pb-2' : 'pb-1'}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-xs font-semibold uppercase tracking-wide ${config.color}`}>
                  {config.label}
                </span>
                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${statusColor}`}>
                  {statusLabel}
                </span>
                <span className="text-[10px] text-slate-500 ml-auto">
                  {formatTimeAgo(activity.timestamp)}
                </span>
              </div>

              <p className={`text-sm text-slate-300 capitalize ${compact ? '' : 'mb-1'}`}>
                {description}
              </p>

              {/* Source badge */}
              {!compact && (
                <div className="text-[10px] text-slate-500">
                  {getSourceIcon(activity.source)} {activity.source}
                </div>
              )}

              {/* Expandable metadata */}
              {!compact && activity.metadata && Object.keys(activity.metadata).length > 0 && (
                <details className="mt-2">
                  <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-400">
                    View details
                  </summary>
                  <pre className="mt-1.5 text-[11px] bg-slate-900 text-slate-400 p-2 rounded overflow-x-auto">
                    {JSON.stringify(activity.metadata, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// UTILITIES
// =============================================================================

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
}

function formatActivityDescription(activity: ActivityRecord): string {
  let description = activity.category?.replace(/-/g, ' ') || activity.type.toLowerCase();

  if (activity.type === 'ORDER' && activity.metadata?.serviceName) {
    description = activity.metadata.serviceName;
  } else if (activity.type === 'REQUEST' && activity.metadata?.description) {
    description += `: ${activity.metadata.description}`;
  } else if (activity.type === 'ECO' && activity.metadata?.greenStayOptIn) {
    description = 'Green Stay enabled';
  } else if (activity.type === 'HOUSEKEEPING' && activity.metadata?.taskType) {
    description = `${activity.metadata.taskType}${activity.metadata.priority ? ` (${activity.metadata.priority})` : ''}`;
  }

  return description;
}

function getSourceIcon(source: string): string {
  const icons: Record<string, string> = {
    PMS: '🏨',
    GUEST_APP: '📱',
    STAFF: '👤',
    AI: '🤖',
    SYSTEM: '⚙️',
  };
  return icons[source] || '•';
}
